import { Button, Card, Divider, Grid, Stack, TextField, Typography, Alert, Slide, Dialog, DialogContent, DialogTitle, DialogActions } from "@mui/material";
import { Header } from "../common/Header";
import { useTranslation } from "react-i18next";
import { useForm } from "react-hook-form";
import ArrowRightAltOutlinedIcon from '@mui/icons-material/ArrowRightAltOutlined';
import { SvmDatabaseDownload, SvmDatabaseTrain, SvmDatabaseUpload, SvmGetCurrentModel } from "../../services/TrainerServices";
import { EndPoints } from "../../axios/endPoints";
import { useEffect, useState } from "react";
import Loader from "../common/Loader";
import { useAppDispatch } from "../../store/store";
import { enqueueSnackbar } from "../../store/app-slice/app";
import { handleNotificationVariant } from "../notifications/_common";
import React from "react";
import { TransitionProps } from "@mui/material/transitions";

interface FeatureSignificance {
    code: string,
    name: string,
    significance: number
}

interface TrainResults {
    accuracy: number,
    precisionTestDataSet: number,
    precisionTrainDataSet: number,
    sizeTestDataSet: number,
    sizeTrainDataSet: number,
    trainDateTimeUTC: string,
    featureSignificance: FeatureSignificance[]
}

interface ResultResponse {
    result: TrainResults | any,
    success: boolean,
    message: string
}

const TrainingDetails = ({ trainResults, headerText } : { trainResults: TrainResults | null, headerText: string }) => {
    const { t } = useTranslation();

    function colorSignificance(significance: number) {
        if(significance > 0) {
            return '#3a8d00';
        }
        else return '#8d003a';
    }

    return(
    <>
        <Grid mb={2} item style={{ width: '100%' }}>
            
        </Grid>
        <Card sx={{  display: 'flex', flexDirection: { xs: "column", md: "row" } }}>
            <Grid item p={3}>
                <Typography variant="h5">{headerText}</Typography>
                <Typography variant="subtitle1">{t('translations:MODEL_TRAIN_ACCURACY')}{(trainResults!.accuracy * 100).toFixed(2)}%</Typography>
                <Typography variant="subtitle1">{t('translations:MODEL_TRAIN_PRECISION_TRAIN')}{(trainResults!.precisionTrainDataSet * 100).toFixed(2)}%</Typography>
                <Typography variant="subtitle1">{t('translations:MODEL_TRAIN_PRECISION_TEST')}{(trainResults!.precisionTestDataSet * 100).toFixed(2)}%</Typography>
                <Typography variant="subtitle1">{t('translations:MODEL_TRAIN_SIZE_TRAIN')}{trainResults!.sizeTrainDataSet}</Typography>
                <Typography variant="subtitle1">{t('translations:MODEL_TRAIN_SIZE_TEST')}{trainResults!.sizeTestDataSet}</Typography>
                <Typography variant="subtitle1">{t('translations:MODEL_TRAIN_DATETIME')}{trainResults!.trainDateTimeUTC}</Typography>
            </Grid>
            {
                trainResults?.featureSignificance && trainResults.featureSignificance?.length > 0 ? 
                <>
                    <Grid item p={3}>
                        <Divider orientation="vertical"/>
                    </Grid>
                    <Grid item p={3} mr={3} sx={{ overflowX: 'auto' }}>
                        <Typography variant="h5">{t('translations:FEATURE_SIGNIFICANCE_CARD_TITLE')}</Typography>
                        <Grid container direction="column">
                            {trainResults?.featureSignificance?.map((el: FeatureSignificance, index: number) => (
                                <Grid key={el.name} container item xs={12} spacing={2}>
                                    <Grid item xs={9}>
                                        <Typography variant="body1">{index + 1}. {t(`translations:${el.name}`)}:</Typography>
                                    </Grid>
                                    <Grid item xs={3}>
                                        <Typography color={colorSignificance(el.significance)} variant="body1">{el.significance > 0 ? `+${el.significance}` : el.significance}</Typography>
                                    </Grid>
                                </Grid>
                            ))}
                        </Grid>
                    </Grid>
                </>
                : ''
            }
        </Card>
    </>
    )
}

const Transition = React.forwardRef(function Transition(
    props: TransitionProps & {
      children: React.ReactElement<any, any>;
    },
    ref: React.Ref<unknown>,
  ) {
    return <Slide direction="up" ref={ref} {...props} />;
});

const ViewResultsDialog = ({ open, trainResults, handleClose } : { open: boolean, trainResults: TrainResults | null, handleClose: any }) => {
    const { t } = useTranslation();
    return(
        <Dialog TransitionComponent={Transition} open={open} onClose={handleClose} fullWidth maxWidth="md">
          <DialogContent>
            <TrainingDetails trainResults={trainResults} headerText={t('translations:MODEL_TRAIN_RESULTS')}/>
          </DialogContent>
          <DialogActions>
            <Button onClick={handleClose}>{t('translations:CLOSE_BUTTON')}</Button>
          </DialogActions>
      </Dialog>
    )
}

const defaultValues = { file: null }
const Trainer = () => {
    const dispatch = useAppDispatch();
    const { t } = useTranslation();
    const { getValues, handleSubmit, register, watch, reset } = useForm({ defaultValues });
    const [openViewResultsDialog, setOpenViewResultsDialog] = useState<boolean>(false);
    const [trainResults, setTrainResults] = useState<TrainResults | null>(null);
    const [uploadFileResult, setUploadFileResult] = useState<ResultResponse | null>(null);
    const [currentModelInfo, setCurrentModelInfo] = useState<TrainResults | null>(null);
    const [loading, setLoading] = useState(false);
    const [reloadCurrentModel, setReloadCurrentModel] = useState(0);

    const handleUpload = () => 
    {
        if(getValues("file"))
        {
            var file = getValues("file")![0];
            setLoading(true);
            SvmDatabaseUpload(EndPoints.TRAINER_UPLOAD_FILE, file).then((response) => {
                setLoading(false);
                if(response) {
                    setUploadFileResult(response);
                    if (response.success) {
                        reset();
                    }
                }
            });
        }
    }

    const handleTrain = () => {
        setLoading(true);
        SvmDatabaseTrain(EndPoints.TRAINER_TRAIN_MODEL).then((response) => {
            setLoading(false);
            if(response && response.success) {
                setTrainResults(response.result);
                setOpenViewResultsDialog(true);
            }
            dispatch(enqueueSnackbar({ message: t(`translations:${response.message}`), options: { variant: handleNotificationVariant(response.success) } }))
        })
    }

    function handleCloseViewResults() {
        setOpenViewResultsDialog(false);
        setReloadCurrentModel((value) => value + 1);
    }

    useEffect(() => {
        setLoading(true);
        SvmGetCurrentModel(EndPoints.TRAINER_GET_CURRENT_MODEL).then((response) => {
            if(response && response.success) {
                setCurrentModelInfo(response.result);
            }
            setLoading(false);
        })
    }, [reloadCurrentModel])

    return(
        <Card style={{ marginTop: '5px'}}>
            { loading ? <Loader open={loading}/> : ''}
            <Header title={t('translations:TRAINER_TITLE')}/>
            <Grid spacing={3} p={4} container direction="column" display="flex" justifyContent="center" alignItems="center">
                <Grid item>
                    <Button 
                        className="button-color" 
                        variant="contained" 
                        size="large"
                        onClick={() => {
                            setLoading(true);
                            SvmDatabaseDownload(EndPoints.TRAINER_DOWNLOAD_DATABASE).then(() => setLoading(false));
                        }}
                        >
                            {t('translations:MODEL_TRAINING_DOWNLOAD')}
                        </Button>
                </Grid>
                <Grid item>
                    <form onSubmit={handleSubmit(handleUpload)}>
                        <Stack direction="row" spacing={3} sx={{ flexDirection: { xs: "column", md: "row" }}}>
                            <TextField {...register('file')} type="file" name="file" variant='outlined' className='text-field-vc text-field-vc-toolbar'/>
                            <Button 
                                disabled={!watch("file")}
                                style={watch('file') ? { color: 'white' } : {}}
                                className={watch('file') ? "button-color" : "btn-primary-outline tbl-toolbar"}
                                size="large" 
                                variant="outlined" 
                                startIcon={<ArrowRightAltOutlinedIcon style={{ transform: "rotate(180deg)" }} />} 
                                type="submit"
                            >{t('translations:MODEL_TRAINING_UPLOAD')}</Button>
                        </Stack>
                    </form> 
                </Grid>
                <Grid item>
                {
                    uploadFileResult ? 
                    <>
                        <Alert onClose={() => setUploadFileResult(null)} variant="outlined" severity={uploadFileResult.success ? 'success' : 'error'}>
                            {t('translations:MODEL_TRAINING_UPLOAD_RESULT')}{t(`translations:${uploadFileResult.message}`)}
                        </Alert>
                    </>
                    :
                    ''
                }
                </Grid>
                <Grid item>
                    <Button onClick={() => {
                        handleTrain();
                    }} className="button-color" variant="contained" size="large">{t('translations:MODEL_TRAINING_TRAIN')}</Button>
                </Grid>
                {
                    currentModelInfo ? <TrainingDetails trainResults={currentModelInfo} headerText={t('translations:MODEL_TRAIN_CURRENT_MODEL_INFO')}/> : ''
                }
                { openViewResultsDialog ? <ViewResultsDialog open={openViewResultsDialog} trainResults={trainResults} handleClose={handleCloseViewResults}/> : '' }
            </Grid>
        </Card>
    )
}

export default Trainer;